import type { AxiosRequestConfig } from "axios";
import { axiosInstance } from "./axios-instance";

/**
 * Global fetcher function for SWR
 * @param url API endpoint
 * @param config Optional axios config
 * @returns Promise with response data
 */
export const fetcher = async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
  const response = await axiosInstance.request<T>({
    url,
    method: config?.method || "GET",
    ...config,
  });

  return response.data;
};

/**
 * POST request fetcher
 * @param url API endpoint
 * @param data Request payload
 * @param config Optional axios config
 * @returns Promise with response data
 */
export const postFetcher = <T, D = unknown>(
  url: string,
  data: D,
  config?: AxiosRequestConfig,
): Promise<T> => {
  return fetcher<T>(url, { ...config, method: "POST", data });
};

/**
 * PUT request fetcher
 * @param url API endpoint
 * @param data Request payload
 * @param config Optional axios config
 * @returns Promise with response data
 */
export const putFetcher = <T, D = unknown>(
  url: string,
  data: D,
  config?: AxiosRequestConfig,
): Promise<T> => {
  return fetcher<T>(url, { ...config, method: "PUT", data });
};

/**
 * PATCH request fetcher
 * @param url API endpoint
 * @param data Request payload
 * @param config Optional axios config
 * @returns Promise with response data
 */
export const patchFetcher = <T, D = unknown>(
  url: string,
  data: D,
  config?: AxiosRequestConfig,
): Promise<T> => {
  return fetcher<T>(url, { ...config, method: "PATCH", data });
};

/**
 * DELETE request fetcher
 * @param url API endpoint
 * @param config Optional axios config
 * @returns Promise with response data
 */
export const deleteFetcher = <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
  return fetcher<T>(url, { ...config, method: "DELETE" });
};
